import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, memo } from "react";
import MuiRadio from "@material-ui/core/Radio";
import MuiCheckbox from "@material-ui/core/Checkbox";
import MuiSwitch from "@material-ui/core/Switch";
import MuiRadioGroup from "@material-ui/core/RadioGroup";
import MuiFormGroup from "@material-ui/core/FormGroup";
import MuiToggleButton from "@material-ui/lab/ToggleButton";
import MuiToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import MuiFormControl from "@material-ui/core/FormControl";
import MuiFormLabel from "@material-ui/core/FormLabel";
import MuiFormControlLabel from "@material-ui/core/FormControlLabel";
import { identityRecordOfFieldEditorOptions, getFieldError, defaultGetItemLabel, defaultGetItemValue } from "./utils";
import { makeStyles, ThemeProvider } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ClearIcon from "@material-ui/icons/Clear";
import merge from "lodash/merge";
import clsx from "clsx";
import { FormHelperText, Typography } from "@material-ui/core";
import { useAriaInvalidProps } from "./aria-error";
const variants = [
    "standard",
    "switch",
    "button",
    "toggleButton",
    "toggleButtonNoIcons"
];
const colors = ["primary", "secondary", "default"];
const useStyles = makeStyles((theme) => ({
    defaultControl: {},
    checkboxControl: {},
    switch: {},
    checkbox: {},
    radio: {},
    innerLabel: {
        display: "block"
    },
    buttonGroup: {
        display: "flex",
        flexWrap: "wrap",
        marginTop: 8,
        marginBottom: 8
    },
    toggleButtonGroup: {
        flexGrow: 1,
        flexShrink: 0
    },
    toggleButtonSwitch: {
        color: theme.palette.primary.main,
        backgroundColor: "#fff",
        marginTop: 12,
        fontSize: 15
    },
    formGroup: {
        padding: "10px 0"
    },
    radioControlLabel: {
        padding: "9px 0"
    },
    fullWidth: {
        flexGrow: 1
    },
    helperTextIconButton: {
        padding: 6,
        margin: "-6px 0px"
    },
    row: {
        width: "auto"
    }
}), { name: "DploySelectionField" });
const toggleButtonSwitchTheme = (theme) => {
    const overrides = {
        ...theme,
        overrides: {
            MuiToggleButton: {
                root: {
                    "&:not(:first-child)": {
                        border: `1px solid ${theme.palette.grey[700]}`,
                        borderLeft: `1px solid ${theme.palette.grey[700]}`
                    }
                }
            },
            ...theme.overrides
        }
    };
    return merge({}, theme, overrides);
};
export function SelectionField(props) {
    const { field, form, helperText, disabled, options, errorDisplay, customOnChange, pageToOpen, goto, ...rest } = props;
    const { name, value } = field;
    const { errors, touched, isSubmitting, setFieldValue } = form;
    const onChange = useCallback((selected) => {
        if (customOnChange)
            customOnChange(selected);
        setFieldValue(name, selected);
        if (pageToOpen) {
            if (Number(pageToOpen) >= 0)
                goto(Number(pageToOpen));
        }
        else {
            form.setFieldTouched(name, true);
        }
    }, [setFieldValue, customOnChange, name, pageToOpen, goto, form]);
    return (_jsx(DploySelectionField, { ...rest, ...getFieldError(errors, touched, field.name, errorDisplay), name: name, value: value, disabled: isSubmitting || disabled, onChange: onChange }));
}
function DploySelectionFieldInternal({ onChange, value, label, helperText, disabled, error, items = [], getItemLabel = defaultGetItemLabel, getItemValue = defaultGetItemValue, valueKey, labelKey, getItemSuggestions, onSelectItem, fullWidth, classes, variant, multiple, searchable, clearable, manual, pending, emptyEqualsZero, formatString, row, color, italicText, boldText, modalText, textAlign, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...props }) {
    const onButtonChange = useCallback((_, selected) => onChange?.(selected == null && !clearable ? value : selected), [onChange, value, clearable]);
    const onMultipleChange = useCallback((selected) => {
        let list = [];
        if (!Array.isArray(value))
            list = selected ? [selected] : [];
        else
            list = value?.includes(selected)
                ? value?.filter(v => v !== selected)
                : value
                    ? [...value, selected]
                    : [selected];
        if (onChange)
            onChange(list);
    }, [onChange, value]);
    const selectionClasses = useStyles(props);
    let formGroup;
    switch (variant) {
        case "button": {
            formGroup = (_jsx(MuiToggleButtonGroup, { className: selectionClasses.buttonGroup, value: value, exclusive: !multiple, onChange: onButtonChange, color: color ?? "standard", "aria-disabled": disabled, children: items.map(item => {
                    const itemValue = getItemValue(item);
                    let itemLabel = getItemLabel(item);
                    if (typeof itemLabel === "string")
                        itemLabel = itemLabel.split("|")[0];
                    return (_jsx(MuiToggleButton, { disabled: disabled, "data-option-label": itemLabel, "data-option-value": itemValue, className: selectionClasses.toggleButtonGroup, value: itemValue, children: itemLabel }, String(itemLabel)));
                }) }));
            break;
        }
        case "toggleButtonNoIcons":
        case "toggleButton": {
            const notSelected = items.find(i => String(getItemValue(i)) !== String(value));
            formGroup = (_jsx(ThemeProvider, { theme: toggleButtonSwitchTheme, children: _jsxs(MuiToggleButton, { disabled: disabled, 
                    // selected={true}
                    onChange: () => {
                        onChange(getItemValue(notSelected));
                    }, value: value, className: selectionClasses.toggleButtonSwitch, children: [variant === "toggleButtonNoIcons" ? null : value ? (_jsx(ClearIcon, {})) : (_jsx(AddIcon, {})), notSelected && getItemLabel(notSelected)] }) }));
            break;
        }
        default: {
            const Control = variant === "switch" ? MuiSwitch : multiple ? MuiCheckbox : MuiRadio;
            const Group = multiple ? MuiFormGroup : MuiRadioGroup;
            formGroup = (_jsx(Group, { row: row, ...props, className: selectionClasses.formGroup, children: items.map((item, idx) => {
                    const itemValue = getItemValue(item);
                    let itemLabel = getItemLabel(item);
                    if (typeof itemLabel === "string")
                        itemLabel = itemLabel.split("|")[0];
                    return (_jsx(FormControlLabel, { "data-option-label": itemLabel, "data-option-value": itemValue, onChange: multiple ? onMultipleChange : onChange, className: clsx(selectionClasses.defaultControl, {
                            [selectionClasses.checkboxControl]: variant !== "switch" && multiple,
                            [selectionClasses.fullWidth]: fullWidth,
                            [selectionClasses.row]: row
                        }), value: itemValue, disabled: disabled, label: _jsx(Typography, { component: "span", className: clsx(selectionClasses.innerLabel, {
                                [selectionClasses.radioControlLabel]: variant !== "switch" && !multiple
                            }), children: itemLabel }), control: _jsx(Control, { className: clsx({
                                [selectionClasses.switch]: variant === "switch",
                                [selectionClasses.checkbox]: variant !== "switch" && multiple,
                                [selectionClasses.radio]: variant !== "switch" && !multiple
                            }), checked: multiple
                                ? value?.includes(itemValue) ?? false
                                : value === itemValue, color: color }) }, String(typeof itemLabel === "object" ? itemValue : itemLabel)));
                }) }));
        }
    }
    const labelStyle = merge({ width: "100%" }, boldText ? { fontWeight: "bold" } : {}, italicText ? { fontStyle: "italic" } : {});
    const ariaInvalidProps = useAriaInvalidProps(!!error);
    return (_jsxs(MuiFormControl, { variant: "standard", component: "fieldset", error: error, disabled: disabled, fullWidth: fullWidth, classes: classes, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...ariaInvalidProps, children: [label != null && (_jsx(MuiFormLabel, { component: "legend", style: labelStyle, children: label })), formGroup, error && helperText && (_jsx(FormHelperText, { error: true, children: helperText }))] }));
}
DploySelectionFieldInternal.displayName = "DploySelectionField";
export const DploySelectionField = memo(DploySelectionFieldInternal);
const FormControlLabel = (props) => {
    const { onChange: propsOnChange, value } = props;
    const onChange = useCallback(() => propsOnChange(value), [value, propsOnChange]);
    return (_jsx(MuiFormControlLabel, { ...props, value: String(props.value), onChange: onChange }));
};
export const EditorSelectionFields = identityRecordOfFieldEditorOptions({
    SelectionField: {
        editableOptions: {
            variant: variants,
            color: colors,
            horizontal: true,
            multiple: true,
            optionPairs: true
        }
    }
});
//# sourceMappingURL=SelectionField.js.map